import { clamp } from '../util/units.js'

/**
 * Where the camera is pointing, per view, and how the mouse moves it.
 *
 * WHY THIS EXISTS
 * Mouse look used to work in exactly two of the six views. In the other four
 * the drag handler ran, did nothing, and gave no indication why — so the chase
 * view in particular looked broken rather than fixed, because a third-person
 * camera you cannot move is the one thing nobody expects.
 *
 * The fix is not "add three more special cases". It is to notice that all six
 * views want the same two things — turn the view, and change how much of the
 * world is in it — and that they only differ in what those two things MEAN:
 *
 *   ORBIT and CHASE are positional. Dragging swings the camera around the
 *   aeroplane and the wheel moves it closer or further away.
 *
 *   COCKPIT, LSO, PLAT and TOWER are angular. The camera is somewhere fixed —
 *   a seat, a platform, a hole in the deck, the island — and dragging turns
 *   your head or the camera mount. Nobody picks up the LSO and carries him
 *   somewhere else, so the wheel is a zoom lens instead.
 *
 * The angular views apply their offset AFTER the automatic aim, which is what
 * makes it useful rather than annoying: drag twenty degrees left of the jet and
 * you stay twenty degrees left of it as it moves, instead of losing the track.
 *
 * Kept free of three.js so the clamping, the wrapping and the orbit vector can
 * be tested without a graphics context.
 */

/** How the mouse behaves in each view. */
export const CONTROL = {
  /** Camera orbits the aeroplane; wheel changes range. */
  ORBITAL: 'orbital',
  /** Camera stays put and turns; wheel changes field of view. */
  ANGULAR: 'angular',
}

/**
 * Per-view defaults and limits.
 *
 * `yaw` and `pitch` are radians of user offset and always start at zero, so
 * "recentre" means "set them to zero" in every view and needs no special cases.
 */
export const VIEW_SETUP = {
  cockpit: {
    control: CONTROL.ANGULAR,
    fov: 58,
    fovRange: [32, 78],
    // A near plane is a plane, so it slices geometry the moment you turn
    // toward it — structure a foot from your shoulder is comfortably beyond a
    // 0.28 m near plane when you are looking ahead, and inside it when you look
    // at it. Hence a much closer one in the cockpit, which is the only view
    // where anything is within arm's reach.
    near: 0.1,
    // Wide, because looking over your shoulder at the boat in the break is a
    // real thing a pilot does and the whole reason mouse look exists here.
    yawRange: [-2.705, 2.705], // +/- 155 degrees
    pitchRange: [-1.396, 1.309], // -80 to +75 degrees
  },
  chase: {
    control: CONTROL.ORBITAL,
    fov: 52,
    // Nothing is within reach in an external view, so the near plane can be
    // pushed out — which buys back the depth precision the cockpit spends.
    near: 0.5,
    fovRange: [24, 70],
    dist: 16,
    distRange: [7, 260],
    // Default: a little above and directly astern.
    pitch: 0.2,
    pitchRange: [-0.9, 1.25],
  },
  orbit: {
    control: CONTROL.ORBITAL,
    fov: 50,
    // As chase.
    near: 0.5,
    fovRange: [24, 70],
    dist: 34,
    distRange: [12, 320],
    yaw: 0.6,
    pitch: 0.22,
    pitchRange: [-1.2, 1.35],
  },
  lso: {
    control: CONTROL.ANGULAR,
    fov: 26,
    // Standing on the platform: the nearest thing is the deck edge.
    near: 1.0,
    // Down to 8 degrees: the LSO is reading a jet a mile away and the whole
    // point of the view is seeing what he sees.
    fovRange: [8, 65],
    yawRange: [-2.36, 2.36],
    pitchRange: [-1.05, 1.05],
  },
  plat: {
    control: CONTROL.ANGULAR,
    fov: 34,
    // Buried in the deck looking aft.
    near: 1.0,
    fovRange: [10, 70],
    yawRange: [-2.36, 2.36],
    pitchRange: [-1.05, 1.05],
  },
  tower: {
    control: CONTROL.ANGULAR,
    fov: 32,
    // Up on the island, and everything is a long way off.
    near: 1.0,
    fovRange: [8, 70],
    // A full circle: the tower looks all the way round the ship, and half a
    // turn either way is exactly that.
    yawRange: [-Math.PI, Math.PI],
    pitchRange: [-1.05, 1.05],
  },
}

/** A fresh look state for one view, at its documented default. */
export function makeViewState(key) {
  const s = VIEW_SETUP[key]
  return {
    key,
    yaw: s.yaw ?? 0,
    pitch: s.pitch ?? 0,
    dist: s.dist ?? 0,
    fov: s.fov,
    near: s.near ?? 0.5,
  }
}

/** Every view's state, keyed the same way as VIEW_SETUP. */
export function makeAllViewStates() {
  const out = {}
  for (const key of Object.keys(VIEW_SETUP)) out[key] = makeViewState(key)
  return out
}

/** Put one view back to its default without touching the others. */
export function recentre(state) {
  Object.assign(state, makeViewState(state.key))
  return state
}

/** Drag. dx and dy are pixels. */
export function applyDrag(state, dx, dy) {
  const s = VIEW_SETUP[state.key]

  // Sensitivity scales with the field of view, which is the difference between
  // a zoom that is usable and one that is not: at 8 degrees on the LSO platform
  // a fixed pixel-to-radian rate flings the view off the jet in half a drag,
  // because every pixel is worth seven times what it is worth at 58.
  const gain = (0.0026 * state.fov) / 58

  if (s.control === CONTROL.ORBITAL) {
    // Free-running: you can wind an orbit round as many times as you like.
    state.yaw = wrapPi(state.yaw - dx * gain * 1.9)
    state.pitch = clamp(state.pitch + dy * gain * 1.5, ...s.pitchRange)
  } else {
    state.yaw = clamp(state.yaw - dx * gain, ...s.yawRange)
    state.pitch = clamp(state.pitch - dy * gain, ...s.pitchRange)
  }
  return state
}

/** Wheel. `delta` is the browser's deltaY: positive scrolls "away". */
export function applyZoom(state, delta) {
  const s = VIEW_SETUP[state.key]
  if (s.control === CONTROL.ORBITAL) {
    state.dist = clamp(state.dist * (1 + delta * 0.001), ...s.distRange)
  } else {
    state.fov = clamp(state.fov * (1 + delta * 0.0012), ...s.fovRange)
  }
  return state
}

/**
 * Where an orbital camera sits, relative to the thing it is looking at.
 *
 * Expressed in the AEROPLANE'S HEADING FRAME rather than the world's, so the
 * camera stays behind the jet as it turns instead of being left pointing at
 * empty ocean halfway through the break. Only the heading is used — taking the
 * roll as well would barrel-roll the camera through the break and make the
 * horizon useless as a reference, which is the whole reason you are in this
 * view.
 *
 * @param {number} headingRad the aircraft's heading
 * @param {number} yaw user offset, radians, 0 = directly astern
 * @param {number} pitch user offset, radians, positive = above
 * @param {number} dist metres
 * @returns {{x:number,y:number,z:number}} offset from the aircraft, world axes
 */
export function orbitOffset(headingRad, yaw, pitch, dist) {
  // Heading h means the nose points at (sin h, 0, -cos h), so "astern" is the
  // negative of that.
  const bx = -Math.sin(headingRad)
  const bz = Math.cos(headingRad)
  // Swing it round the vertical by the user's yaw.
  const ca = Math.cos(yaw)
  const sa = Math.sin(yaw)
  const dx = bx * ca + bz * sa
  const dz = -bx * sa + bz * ca

  const cp = Math.cos(pitch)
  return { x: dx * cp * dist, y: Math.sin(pitch) * dist, z: dz * cp * dist }
}

/** Wrap to (-pi, pi] so a free orbit does not accumulate to a huge number. */
export function wrapPi(a) {
  const twoPi = Math.PI * 2
  let x = (a + Math.PI) % twoPi
  if (x < 0) x += twoPi
  return x - Math.PI
}
